// Burst-coalescing ResizeObserver wrapper for canvas-backed surfaces.
//
// A divider drag fires a ResizeObserver tick on every frame. Surfaces
// that respond by repainting a full canvas (and shipping its texture to
// the GPU) saturate weaker GPUs when that happens ~60×/s — the panel
// resize lag captured in the 2026-06-10 staging traces was exactly this,
// stacked across every chart plus the ScopeColumns strip.
//
// A tick is classified as part of a continuous gesture when ANY of:
// - a resize gesture class is on <body> (the panel/sidebar/column
//   dividers set these for the duration of the drag — see styles.css);
// - it arrived within `burstMs` of the previous tick;
// - the last settled redraw fired within `fireCooldownMs`.
//
// The gesture classes are the primary freeze for in-app drags: while
// one is up, no redraw fires at all — ticks pin on entry and the one
// crisp redraw lands after the class clears, regardless of cadence.
// They exist because compositor back-pressure paces ResizeObserver
// ticks at the GPU's frame latency, so when a redraw itself costs
// 200ms+ the ticks arrive too far apart to look like a burst — gap-
// based classification alone full-redrew on every tick and re-created
// the exact lag this exists to prevent (the 2026-06-10 follow-up
// trace: 100–430ms GPU tasks at 150–430ms tick cadence, every tick
// "leading").
//
// The cooldown rule is the damper for resize sources that carry no
// gesture class (OS window-edge drags). It cannot fully coalesce a
// slow tick stream — consecutive cooldown-classified ticks have gaps
// > burstMs ≥ settleMs, so a settle always lands between them and the
// burst re-enters per cycle — but it guarantees a tick never triggers
// an immediate redraw on the heels of the previous one: the surface
// pins first and the redraw waits for the settle timer.
//
// Semantics:
// - The first size change after a quiet period (no gesture class, no
//   recent tick, no recent fire) fires `onSettled` immediately, so
//   one-shot layout changes (panel collapse, window snap) stay crisp.
// - On entering a gesture, `onBurstStart` fires once — pin / freeze
//   the surface there. With a gesture class up, that happens on the
//   FIRST tick: no redraw at drag start at all.
// - `onSettled` fires once, `settleMs` after the last tick — but never
//   while a gesture class is still up; the timer re-arms until the
//   drag actually ends (classes are removed on pointerup, so a stuck
//   class would freeze redraws — keep those handlers exit-safe).
// - `settleMs` must stay ≤ `burstMs`: a pending settle timer implies
//   the next tick still classifies as part of the same burst.
//
// Returns a disconnect function; safe to call in effect cleanups.

// Body classes the resize affordances hold for the duration of a drag
// (SplitPage detail divider, App sidebar, column resizers). Read here so
// every observed surface freezes for the whole gesture even when the
// GPU paces ticks slower than any gap heuristic could classify.
const RESIZE_GESTURE_CLASSES = [
  "is-resizing-detail-panel",
  "is-resizing-sidebar",
  "is-resizing-column",
];

function resizeGestureActive() {
  if (typeof document === "undefined" || !document.body) return false;
  const cl = document.body.classList;
  return RESIZE_GESTURE_CLASSES.some((c) => cl.contains(c));
}

export function observeSettledSize(node, onSettled, options = {}) {
  const { burstMs = 200, settleMs = 150, fireCooldownMs = 350, onBurstStart } = options;
  if (typeof ResizeObserver === "undefined") return () => {};

  let lastTickAt = -Infinity;
  let lastFireAt = -Infinity;
  let timer = 0;
  let inBurst = false;

  const settle = () => {
    timer = 0;
    // Mid-gesture quiet spell (pointer holding still): stay frozen and
    // keep checking — the real settle lands ≤ settleMs after pointerup
    // removes the gesture class.
    if (resizeGestureActive()) {
      timer = setTimeout(settle, settleMs);
      return;
    }
    inBurst = false;
    lastFireAt = performance.now();
    onSettled();
  };

  const observer = new ResizeObserver(() => {
    const now = performance.now();
    const continues =
      resizeGestureActive() ||
      now - lastTickAt < burstMs ||
      now - lastFireAt < fireCooldownMs;
    lastTickAt = now;
    if (!continues) {
      // With settleMs ≤ burstMs a timer can't be pending here, but a
      // caller that violates the invariant must not get a double fire.
      clearTimeout(timer);
      settle();
      return;
    }
    if (!inBurst) {
      inBurst = true;
      onBurstStart?.();
    }
    clearTimeout(timer);
    timer = setTimeout(settle, settleMs);
  });
  observer.observe(node);

  return () => {
    observer.disconnect();
    clearTimeout(timer);
  };
}
